<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="show" class="intercom-modal-overlay" @click="handleOverlayClick">
        <Transition name="modal-slide">
          <div
            v-if="show"
            class="intercom-modal"
            :style="modalStyle"
            v-bind="$attrs"
            @click.stop
            role="dialog"
            aria-modal="true"
            :aria-labelledby="titleId"
          >
            <!-- Header -->
            <div v-if="$slots.header || title" class="intercom-modal-header">
              <slot name="header">
                <h2 :id="titleId" class="intercom-modal-title">{{ title }}</h2>
              </slot>
              <button
                v-if="closable"
                class="intercom-modal-close"
                @click="handleClose"
                aria-label="Close"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <!-- Body -->
            <div class="intercom-modal-body">
              <slot />
            </div>

            <!-- Footer -->
            <div v-if="$slots.footer" class="intercom-modal-footer">
              <slot name="footer" />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
defineOptions({ inheritAttrs: false })

import { computed, watch, onMounted, onUnmounted, nextTick, ref } from 'vue'

const props = defineProps({
  /**
   * Show/hide modal
   */
  show: {
    type: Boolean,
    default: false,
  },
  /**
   * Modal title
   */
  title: {
    type: String,
    default: '',
  },
  /**
   * Modal size preset (sm, md, lg, xl)
   */
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg', 'xl'].includes(value),
  },
  /**
   * Modal width (overrides size if provided)
   */
  width: {
    type: [String, Number],
    default: null,
  },
  /**
   * Close on overlay click (default false to prevent accidental data loss in forms)
   */
  closeOnOverlay: {
    type: Boolean,
    default: false,
  },
  /**
   * Show close button
   */
  closable: {
    type: Boolean,
    default: true,
  },
  /**
   * Close on escape key
   */
  closeOnEscape: {
    type: Boolean,
    default: true,
  },
})

// Size presets
const sizeWidths = {
  sm: '400px',
  md: '600px',
  lg: '800px',
  xl: '1000px',
}

const emit = defineEmits(['update:show', 'close'])

const titleId = `modal-title-${Math.random().toString(36).substr(2, 9)}`
const previousFocus = ref(null)

// Focusable element selector for focus trap
const FOCUSABLE = 'input:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"]), a[href]'

const modalStyle = computed(() => {
  // Use explicit width if provided, otherwise use size preset
  const widthValue = props.width !== null
    ? (typeof props.width === 'number' ? `${props.width}px` : props.width)
    : sizeWidths[props.size]
  return { maxWidth: widthValue }
})

const handleClose = () => {
  emit('update:show', false)
  emit('close')
}

const handleOverlayClick = () => {
  if (props.closeOnOverlay) {
    handleClose()
  }
}

const handleEscape = (e) => {
  if (!props.show || !props.closeOnEscape || e.key !== 'Escape') return

  // If any child already handled this Escape (e.g., dropdown closing), skip
  if (e.defaultPrevented) return

  // If a dropdown/popover/select is open, let it close first — don't close the modal.
  // Checks for: IntercomSelect (custom), naive-ui select, HSN search dropdown
  const openDropdown = document.querySelector(
    '.intercom-select-container--open, .intercom-select-dropdown, .n-base-select-menu, .hsn-dropdown'
  )
  if (openDropdown) return

  handleClose()
}

// Focus trap: Tab cycles within the modal instead of escaping to the page behind
const handleFocusTrap = (e) => {
  if (!props.show || e.key !== 'Tab') return

  // Find the modal element in the DOM
  const modal = document.querySelector('.intercom-modal')
  if (!modal) return

  const focusable = [...modal.querySelectorAll(FOCUSABLE)].filter(
    (el) => el.offsetParent !== null // visible only
  )
  if (focusable.length === 0) return

  const first = focusable[0]
  const last = focusable[focusable.length - 1]

  if (e.shiftKey) {
    // Shift+Tab: wrap from first to last
    if (document.activeElement === first || !modal.contains(document.activeElement)) {
      e.preventDefault()
      last.focus()
    }
  } else {
    // Tab: wrap from last to first
    if (document.activeElement === last || !modal.contains(document.activeElement)) {
      e.preventDefault()
      first.focus()
    }
  }
}

// Lock body scroll + auto-focus when modal opens
watch(
  () => props.show,
  (newValue) => {
    if (newValue) {
      document.body.style.overflow = 'hidden'
      // Save current focus to restore later
      previousFocus.value = document.activeElement
      // Auto-focus first interactive element after modal fully renders.
      // Uses setTimeout to ensure slot content (form fields) and async data
      // (customer lists, product lists) have loaded.
      const attemptFocus = (attempt = 0) => {
        const modal = document.querySelector('.intercom-modal')
        if (!modal) return

        // Priority 1: Element with explicit data-autofocus attribute
        const autoFocusEl = modal.querySelector('[data-autofocus]')
        if (autoFocusEl) {
          const target = autoFocusEl.querySelector(
            'button.intercom-select-trigger:not([disabled]), input:not([disabled]), textarea:not([disabled])'
          ) || autoFocusEl
          if (target && !target.disabled) {
            target.focus()
            return
          }
          // Target is still disabled (async loading) — retry
          if (attempt < 15) {
            setTimeout(() => attemptFocus(attempt + 1), 100)
          }
          return
        }

        // Priority 2: First interactive element in DOM order
        const firstField = modal.querySelector(
          'button.intercom-select-trigger:not([disabled]), ' +
          'input.intercom-select-search, ' +
          'input:not([disabled]):not([type="hidden"]):not(.intercom-modal-close), ' +
          'textarea:not([disabled]), ' +
          'select:not([disabled])'
        )
        if (firstField) {
          firstField.focus()
          return
        }

        // Retry up to 15 times at 100ms (1.5s total — covers API load time)
        if (attempt < 15) {
          setTimeout(() => attemptFocus(attempt + 1), 100)
        }
      }

      nextTick(() => nextTick(() => attemptFocus()))
    } else {
      document.body.style.overflow = ''
      // Restore previous focus
      if (previousFocus.value && typeof previousFocus.value.focus === 'function') {
        nextTick(() => previousFocus.value?.focus())
      }
    }
  }
)

onMounted(() => {
  document.addEventListener('keydown', handleEscape)
  document.addEventListener('keydown', handleFocusTrap)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
  document.removeEventListener('keydown', handleFocusTrap)
  document.body.style.overflow = ''
})
</script>

<style scoped>
/* ========================================
   MODAL OVERLAY
   ======================================== */
.intercom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal-backdrop);
  padding: var(--space-4);
  overflow-y: auto;
}

/* ========================================
   MODAL
   ======================================== */
.intercom-modal {
  width: 100%;
  background: var(--color-white);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  z-index: var(--z-modal);
  max-height: calc(100vh - var(--space-16));
  display: flex;
  flex-direction: column;
}

.intercom-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-6);
  border-bottom: 1px solid var(--color-gray-200);
  flex-shrink: 0;
}

.intercom-modal-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.intercom-modal-close {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--radius-md);
  color: var(--color-gray-500);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.intercom-modal-close:hover {
  background: var(--color-gray-100);
  color: var(--color-charcoal);
}

.intercom-modal-body {
  padding: var(--space-6);
  overflow-y: auto;
  flex: 1;
}

.intercom-modal-footer {
  padding: var(--space-6);
  border-top: 1px solid var(--color-gray-200);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-3);
  flex-shrink: 0;
}

/* ========================================
   TRANSITIONS
   ======================================== */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity var(--transition-base);
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-slide-enter-active,
.modal-slide-leave-active {
  transition: all var(--transition-base);
}

.modal-slide-enter-from,
.modal-slide-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-20px);
}

/* ========================================
   RESPONSIVE
   ======================================== */
@media (max-width: 768px) {
  .intercom-modal {
    max-height: calc(100vh - var(--space-8));
  }

  .intercom-modal-header,
  .intercom-modal-body,
  .intercom-modal-footer {
    padding: var(--space-4);
  }
}
</style>
